import { useEffect, useState } from 'react';
import useCanvasReady from './useCanvasReady';
import { Uqda } from '../../../components/mascot/Uqda';
import { useReducedMotion } from '../../../styles/motion/hooks';
import './HeroCanvas.css';

/**
 * Pose cycle — Uqda runs a little routine on a loop so the hero feels alive.
 * Each step carries an emotion (face) + activity pose under the v2 API.
 * Cycles through the full expressive range:
 *
 *   calm + idle           — settles, breathing
 *   thinking + idle        — eyes up, pondering (v1 "thinking" pose retired)
 *   curious + walking      — shifts feet, slides side-to-side
 *   curious + discovering  — wide eyes + sparks, "found it!"
 *   happy + discovering    — celebration beat (v1 "leaping" pose retired)
 *   sleeping + sleeping    — closed eyes + zZz, takes a quick nap
 *
 * Connections "breathe" by varying 4–6 so satellites pop in and out subtly
 * (more when active, fewer when sleeping). Durations are deliberately
 * uneven to break the rhythm — and on the long side (3.4–4.0 s each) so
 * the routine reads as relaxed rather than twitchy.
 */
const POSE_SEQUENCE = [
  { emotion: 'calm',     pose: 'idle',        duration: 3600, connections: 6 },
  { emotion: 'thinking', pose: 'idle',        duration: 3800, connections: 5 },
  { emotion: 'curious',  pose: 'walking',     duration: 3800, connections: 6 },
  { emotion: 'curious',  pose: 'discovering', duration: 3200, connections: 6 },
  { emotion: 'happy',    pose: 'discovering', duration: 2200, connections: 6 },
  { emotion: 'calm',     pose: 'idle',        duration: 2400, connections: 5 },
  { emotion: 'sleeping', pose: 'sleeping',    duration: 4000, connections: 4 },
];

/**
 * HeroStaticFallback
 *
 * SSR-safe, decorative Uqda mascot that auto-cycles through poses post-
 * hydration to keep the hero visually alive. Initial render is always
 * `idle` so the prerendered HTML matches client first paint (LCP-safe).
 *
 * Shown when the 3D canvas is gated off (prerender, mobile, reduced motion,
 * low-end) AND beneath the lazy canvas as the permanent hero centerpiece.
 * Hard-coded dark-mode colors so the mascot paints correctly even before
 * stylesheets resolve (the hero section is always data-theme="dark").
 *
 * Props:
 *  - engaged: when true (visitor hovers/focuses the CTA), the mascot locks to
 *    a celebratory happy + `discovering` with an amber halo until they look
 *    away.
 */
export const HeroStaticFallback = ({ engaged = false }) => {
  const reduced = useReducedMotion();
  const [cycleIndex, setCycleIndex] = useState(0);

  useEffect(() => {
    if (reduced) return undefined; // No cycling under reduced motion.
    const step = POSE_SEQUENCE[cycleIndex];
    const timer = window.setTimeout(() => {
      setCycleIndex((i) => (i + 1) % POSE_SEQUENCE.length);
    }, step.duration);
    return () => window.clearTimeout(timer);
  }, [cycleIndex, reduced]);

  const cycleStep = POSE_SEQUENCE[cycleIndex] || POSE_SEQUENCE[0];
  const emotion = engaged ? 'happy' : cycleStep.emotion;
  const pose = engaged ? 'discovering' : cycleStep.pose;
  const connections = engaged ? 6 : cycleStep.connections;

  return (
    <div className="hero-canvas-fallback" role="presentation" aria-hidden="true">
      <div
        className={`hero-canvas-fallback__mascot${engaged ? ' hero-canvas-fallback__mascot--engaged' : ''}`}
      >
        <div
          className="hero-canvas-fallback__mascot-body"
          data-pose={pose}
        >
          {/* Bare Uqda (not UqdaAnimated) so the pose swap happens in a
              single render — AnimatePresence's exit/enter window was
              briefly drawing the old and new SVG on top of each other,
              which read as two stacked mascots. The smooth CSS bob +
              pose-specific animations keep it feeling alive without
              needing a crossfade between SVGs. */}
          <Uqda
            size={260}
            emotion={emotion}
            pose={pose}
            connections={connections}
            color="#059669"
            ink="#1a1f1c"
            paper="#f4f7f3"
            accent="#f59e0b"
          />
        </div>
      </div>
    </div>
  );
};

/**
 * HeroCanvas
 *
 * Decorative R3F hero canvas. The component is aria-hidden — the H1 lives
 * outside it as the LCP element. The 3D bundle only loads after
 * useCanvasReady gates pass.
 */
const HeroCanvas = ({ decayProgress = 0 }) => {
  // Light hero variant: the 3D particle field uses additive blending which
  // makes its emerald points invisible on a white background. Skip the 3D
  // chunk entirely — the Uqda mascot's pose cycle + halo carry the section
  // on its own. (decayProgress kept in the signature for future re-enable.)
  void decayProgress;
  const { ref } = useCanvasReady();

  return (
    <div ref={ref} className="hero-canvas-wrapper" aria-hidden="true">
      <HeroStaticFallback />
    </div>
  );
};

export default HeroCanvas;
